<template>
	<label for="name" class="block text-ihh-midnight-gray text-base sm:text-base font-semibold mt-10 flex w-full">Shipping Methods</label>
	<div v-if="loadingShipping" class="w-full flex justify-center">
		<Spinner :small="true" class="w-16 h-16" />
	</div>
	<div v-else>
		<div v-if="!methods.length" class="rounded border border-collapse my-3 p-6 border-ihh-gray/50 overflow-hidden text-base flex flex-col items-center">
			<p>Please enter a shipping address to view shipping quotes.</p>
			<p>All required fields must be completed.</p>

			<!-- Field validation status display -->
			<div v-if="fieldValidationStatus.length > 0" class="mt-4 w-full max-w-md text-sm">
				<div class="text-sm font-medium mb-2">Required Fields Status:</div>
				<ul class="space-y-1">
					<li
						v-for="(field, index) in sortedFieldValidationStatus"
						:key="index"
						class="flex items-center justify-between py-1 px-2 rounded"
						:class="field.validated ? 'bg-green-50' : 'bg-red-50'"
					>
						<span>{{ field.name }}</span>
						<span class="font-bold" :class="field.validated ? 'text-green-600' : 'text-red-600'">
							{{ field.validated ? '✅' : '❌' }}
						</span>
					</li>
				</ul>
			</div>
		</div>
		<div v-else class="rounded border border-collapse mt-4 border-ihh-gray/50 overflow-hidden text-base">
			<table class="text-ihh-midnight-gray w-full">
				<tbody>
					<tr
						v-for="(method, index) in methods"
						:key="index"
						@click="selectMethod(method)"
						:class="['border-b border-ihh-gray/50 cursor-pointer hover:bg-ihh-slate-gray/10', { 'bg-ihh-slate-gray/10 font-semibold': isMethodSelected(method) }]"
					>
						<td class="py-4 pl-6 mr-2">
							<div
								:class="[
									'items-center rounded-full',
									{
										'bg-ihh-dark-salmon ring-1 ring-ihh-dark-salmon ring-offset-4 w-5 h-5 ml-1': isMethodSelected(method),
										'border border-ihh-gray w-7 h-7 bg-ihh-white text-ihh-midnight-gray/70': !isMethodSelected(method),
									},
								]"
							/>
						</td>
						<td class="p-4 flex w-full justify-between gap-x-8 m-auto items-center">
							<div class="flex w-full">{{ formatShippingDescription(method?.description) }}</div>
							<div class="flex font-semibold">${{ method?.cost.toFixed(2) }}</div>
						</td>
					</tr>
				</tbody>
			</table>
		</div>
	</div>
</template>

<script>
import helpers from '../../helpers/checkout.js'

export default {
	name: 'ShippingMethods',
	inject: ['checkout'],

	computed: {
		sortedFieldValidationStatus() {
			if (!this.fieldValidationStatus) return []
			return [...this.fieldValidationStatus].sort((a, b) => {
				// false (unvalidated) comes before true (validated)
				return a.validated === b.validated ? 0 : a.validated ? 1 : -1
			})
		},
	},

	props: {
		methods: {
			type: Array,
			default: () => [],
		},
		selectedMethod: {
			type: Object,
			default: null,
		},
		loadingShipping: {
			type: Boolean,
			default: false,
		},
		fieldValidationStatus: {
			type: Array,
			default: () => [],
		},
	},

	methods: {
		isMethodSelected(method) {
			// Compare by ID instead of object reference
			return this.selectedMethod && method && this.selectedMethod.id === method.id
		},
		async selectMethod(method) {
			this.checkout.selectShipping(method)
			await helpers.selectShippingMethod()
		},
		formatShippingDescription(description, short) {
			const patterns = [
				{
					match: (desc) => desc.includes('USPS'),
					getName: (desc) => {
						if (desc.includes('First-Class Package International Service')) {
							return 'USPS First-Class Package International'
						} else if (desc.includes('Priority Mail International')) {
							return 'USPS Priority Mail International'
						}
						return 'USPS'
					},
					getType: (desc) => {
						if (desc.includes('First-Class Package International Service')) {
							return 'First-Class Mail'
						} else if (desc.includes('Priority Mail International')) {
							return 'Priority Mail'
						} else if (desc.includes('Priority Mail')) {
							return 'Priority Mail'
						}
						return 'Mail'
					},
				},
				{
					match: (desc) => desc.includes('UPS'),
					getName: (desc) => {
						if (desc.includes('Next Day Air')) {
							return 'UPS Next Day Air'
						} else if (desc.includes('2nd Day Air')) {
							return 'UPS 2nd Day Air'
						} else if (desc.includes('Worldwide Expedited')) {
							return 'UPS Worldwide Expedited'
						} else if (desc.includes('Worldwide Saver')) {
							return 'UPS Worldwide Saver'
						} else if (desc.includes('Ground')) {
							return 'UPS Ground'
						}
						return 'UPS'
					},
					getType: (desc) => {
						if (desc.includes('Next Day Air')) {
							return 'Next Day Air'
						} else if (desc.includes('2nd Day Air')) {
							return '2nd Day Air'
						} else if (desc.includes('Expedited')) {
							return 'Expedited'
						} else if (desc.includes('Saver')) {
							return 'Saver'
						} else if (desc.includes('Ground')) {
							return 'Ground'
						}
						return 'Shipping'
					},
				},
				{
					match: (desc) => desc.includes('Local Pickup'),
					getName: () => 'Local Pickup - Liberty Hill, TX',
					getType: () => 'Pickup',
				},
				{
					match: (desc) => desc.includes('Economy'),
					getName: () => 'Economy',
					getType: () => 'Economy',
				},
			]

			const matchedPattern = patterns.find((pattern) => pattern.match(description))

			if (matchedPattern) {
				const name = matchedPattern.getName(description)
				const type = matchedPattern.getType(description)

				// Extract estimated date
				const estimatedDate = description.includes('Estimated:') ? description.split('Estimated:')[1].trim().replace(')', '') : 'No Date'

				// Include type in the name only if they are different and the name doesn't already contain the type
				const nameWithDescription = name !== type && !name.includes(type) ? `${name} (${type})` : name

				if (short) {
					return name
				}

				//return `${nameWithDescription} | Estimated: ${estimatedDate}`
				return estimatedDate === 'No Date' ? `${nameWithDescription}` : `${nameWithDescription} | Estimated: ${estimatedDate}`
			}

			// Fallback for unknown formats
			return description
		},
	},

	// watch: {
	// 	loadingShipping: {
	// 		handler() {
	// 			console.log('ShippingMethods.vue - loadingShipping', this.loadingShipping)
	// 		},
	// 		immediate: true,
	// 	},
	// },
}
</script>

<style scoped></style>
